/**
 * MAIN SEARCH IMPLEMENTATION
 * This is the primary search page component that handles product and florist discovery.
 * 
 * Key Features:
 * - Location-based filtering with Google Maps integration
 * - Product variant handling (sizes, prices)
 * - Delivery radius validation
 * - Comprehensive filter system (price, occasion, category)
 * - Mobile and desktop responsive layouts
 * - URL parameter management for shareable searches
 * 
 * Data Flow:
 * 1. User inputs location or uses URL params
 * 2. Coordinates are extracted and stored
 * 3. Products/Florists are fetched based on location
 * 4. Results are filtered by delivery radius
 * 5. Map updates to show available options
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ProductCard as ProductCardComponent } from "@/components/ProductCard";
import { Loader2, Filter, Store as StoreIcon, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/components/ui/use-toast";
import { useDeliveryCheck } from "@/hooks/useDeliveryCheck";
import { isStoreOpen, getNextOpenTime } from "@/utils/businessHours";
import { Coordinates } from "@/types/google-maps";
import { LocationSearchInput } from "@/components/location/LocationSearchInput";
import { useScreenSize } from "@/hooks/use-screen-size";
import { MobileSearchResults } from "@/components/search/mobile/MobileSearchResults";
import { MobileDeliveryInfo } from "@/components/search/mobile/MobileDeliveryInfo";
import type { AddressWithCoordinates } from "@/types/address";
import { format, isSameDay } from "date-fns";
import { Link } from "react-router-dom";
import { DateFilter } from "@/components/filters/DateFilter";
import { Product, Store, ProductSize } from '@/types/product';
import { haversineDistance } from "@/lib/haversineDistance";
import { TextCursorProximity } from "@/components/ui/text-cursor-proximity";
import AnimatedBackground from "@/components/ui/animated-background";
import { cn } from "@/lib/utils";
import MapView from '@/components/MapView';
import { Cursor } from "@/components/ui/cursor";
import { motion, useSpring, useMotionValue } from "framer-motion";
import { MobileHero } from "@/components/MobileHero";
import { MobileFilterDropdown } from "@/components/search/MobileFilterDropdown";
import { getLocalTimeZone } from "@internationalized/date";
import { debounce } from 'lodash';
import { getDayOfWeekLowercase, getDayOfWeekCapitalized, isDayInArray, getCurrentTimeFormatted, findCutoffTime, isDateToday } from '../utils/dateUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// EMOJIS used in the UI - keeping only the ones we actually use
const EMOJIS = {
  FLOWER: "&#x1F338;",  // 🌸
  TARGET: "&#x1F3AF;",  // 🎯
  TRUCK: "&#x1F69A;",   // 🚚
  LIGHTNING: "⚡",       // ⚡ stays
  CART: "&#x1F6D2;"     // 🛒
} as const;

const decodeHTMLEntities = (text: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "text/html");
  return doc.documentElement.textContent || text;
};

/**
 * FloristProfile type defines the structure of a florist's data
 * including delivery settings and location information
 */
interface FloristProfile {
  id: string;
  store_name: string;
  about_text: string;
  address_details: {
    suburb?: string;
    state?: string;
  };
  logo_url?: string;
  banner_url?: string;
  delivery_settings: {
    max_distance_km: number;          // Maximum delivery radius in kilometers
    same_day_cutoff: string;          // Cutoff time for same-day delivery
    next_day_cutoff_enabled: boolean; // Whether next-day delivery cutoff is enabled
    next_day_cutoff: string;          // Cutoff time for next-day delivery
  };
  rating?: number;
  coordinates: Coordinates;           // Lat/Long coordinates for delivery radius calculation
  storefront_image_url?: string;
  cover_image_url?: string;
}

/**
 * SearchProduct interface defines the structure for products displayed in search results
 */
interface SearchProduct {
  id: string;
  uniqueId: string;
  title: string;
  price: number;
  sale_price?: number | null;
  images: string[];
  stock_status?: 'in_stock' | 'low_stock' | 'out_of_stock';
  /**
   * Florist information with images
   * 
   * Note on image naming conventions:
   * - storefront_image_url: Used as the square profile image (square format)
   * - cover_image_url: Used as the landscape store exterior image (wide format)
   * 
   * In the UI, we use semantic variable names for clarity:
   * - profileSquareImage (mapped to storefront_image_url)
   * - storefrontImage (mapped to cover_image_url)
   */
  florist: {
    id: string;
    store_name: string;
    suburb: string;
    state: string;
    coordinates: Coordinates;
    storefront_image_url?: string;
    cover_image_url?: string;
  };
  selectedVariant: {
    id: string;
    size: string;
    price: number;
  } | null;
  occasions: string[];
  category?: string;
}

// Core filter options
const OCCASIONS = [
  "Birthday",
  "Thank You",
  "Love & Romance",
  "Sympathy",
  "Just Because"
] as const;

const CATEGORIES = [
  "Bouquet",
  "Floral Arrangement",
  "Potted Plant",
  "Single Flower",
  "Other"
] as const;

// Add this CSS class at the top after imports
const emojiStyle = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, "Apple Color Emoji"'
} as const;

/**
 * FloristNameWithPopup Component
 * Displays a florist's name with a hover popup showing additional details
 */
interface FloristNameWithPopupProps {
  florist: {
    store_name?: string;
    suburb?: string;
    state?: string;
    storefront_image_url?: string;
    cover_image_url?: string;
  } | null | undefined;
}

const FloristNameWithPopup: React.FC<FloristNameWithPopupProps> = ({ florist }) => {
  // Return early with a default component if florist data is missing
  if (!florist || !florist.store_name) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-brand-primary/20 text-[13px] text-brand-primary rounded-full font-normal">
        <StoreIcon className="w-3 h-3" />
        Store
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-brand-primary/20 text-[13px] text-brand-primary rounded-full font-normal">
      <StoreIcon className="w-3 h-3" />
      {florist.store_name}
    </span>
  );
};

// Base types for raw database responses
interface RawStoreProfile {
  id: string;
  store_name: string;
  description?: string;
  geocoded_address?: string;
  delivery_radius?: string | number;
  business_hours?: any;
  storefront_image_url?: string;
  cover_image_url?: string;
  suburb?: string;
  state?: string;
  same_day_enabled?: boolean;
  delivery_days?: string[];
  delivery_cutoff_times?: Record<string, string>;
  non_sameday_days?: string[];
}

interface RawProduct {
  id: string;
  title: string;
  description?: string;
  price: string | number;
  category?: string;
  main_image_url?: string;
  gallery_image_urls?: string[];
  store_id: string;
  sizes?: Array<{
    id: string;
    name: string;
    price: string | number;
    is_active: boolean;
  }>;
  occasions?: Array<{
    occasion_id: string;
    occasion?: {
      id: string;
      name: string;
    };
  }>;
}

// Processed types for the application
interface ProcessedStore {
  id: string;
  store_name: string;
  description?: string;
  geocoded_address?: string;
  delivery_radius?: string | number;
  business_hours?: any;
  storefront_image_url?: string;
  cover_image_url?: string;
  suburb?: string;
  state?: string;
  coordinates?: { lat: number; lng: number } | null;
  calculated_distance?: number;
  same_day_enabled?: boolean;
  delivery_days?: string[];
  delivery_cutoff_times?: Record<string, string>;
  non_sameday_days?: string[];
}

interface ProcessedProduct {
  id: string;
  title: string;
  description?: string;
  price: number;
  category?: string;
  main_image_url?: string;
  gallery_image_urls?: string[];
  store: ProcessedStore;
  sizes?: Array<{
    id: string;
    name: string;
    price: number;
    is_active: boolean;
  }>;
  occasions: string[]; // Simplified to just occasion names after processing
}

interface ProductCard {
  id: string;
  title: string;
  description?: string;
  price: number;
  category?: string;
  images: string[];
  store: {
    id: string;
    store_name: string;
    coordinates?: { lat: number; lng: number } | null;
    suburb?: string;
    state?: string;
    delivery_radius?: string | number;
    calculated_distance?: number;
  };
  selectedVariant?: {
    id: string;
    size: string;
    size_name: string;
    price: number;
    is_active: boolean;
  };
  uniqueId?: string;
  occasions: string[];
}

/**
 * Creates map pins from store data with proper coordinate handling
 * @param stores Array of store data
 * @returns Array of formatted map pins with coordinates and range status
 */
const createMapPins = (stores: any[]) => {
  // Create a Set to track processed store IDs
  const processedStoreIds = new Set();
  
  return stores.map(store => {
    // Skip if we've already processed this store
    if (!store || !store.id || processedStoreIds.has(store.id)) {
      return null;
    }
    processedStoreIds.add(store.id);
    
    // Get store coordinates - prefer direct coordinates over geocoded
    const storeCoords = store.coordinates || (() => {
      try {
        const addressData = typeof store.geocoded_address === 'string' 
          ? JSON.parse(store.geocoded_address) 
          : store.geocoded_address;
        
        return addressData?.geometry?.location 
          ? {
              lat: addressData.geometry.location.lat,
              lng: addressData.geometry.location.lng
            }
          : null;
      } catch (e) {
        return null;
      }
    })();

    if (!storeCoords) return null;
    
    // Get location text using direct values
    const locationText = store.suburb && store.state 
      ? `${store.suburb}, ${store.state}`
      : '';
    
    return {
      id: store.id,
      position: storeCoords,
      title: store.store_name,
      subtitle: locationText,
      imageUrl: store.storefront_image_url || '/placeholder.jpg',
      isInRange: store.calculated_distance <= Number(store.delivery_radius)
    };
  }).filter(Boolean);
};

/**
 * Main Search Page Component
 * 
 * This component handles the core search functionality for both products and florists.
 * It manages location-based filtering, delivery radius calculations, and various filter options.
 * 
 * State Management:
 * - Location and coordinates
 * - Filter options (price, occasions, categories)
 * - Delivery type (delivery/pickup)
 * - Search results (products/florists)
 * 
 * @returns React Component
 */
export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const screenSize = useScreenSize();
  const isMobile = screenSize.lessThan('lg');
  const queryClient = useQueryClient();
  const [sortOrder, setSortOrder] = useState<string>(searchParams.get('sortBy') || 'default');
  const [groupBySizeVariant, setGroupBySizeVariant] = useState<boolean>(true); // default to grouped
  
  // URL Parameters
  const location = searchParams.get('location');
  const dateStringFromUrl = searchParams.get('date');
  const minPrice = searchParams.get('minPrice');
  const maxPrice = searchParams.get('maxPrice');
  const deliveryType = searchParams.get('deliveryType') as 'delivery' | 'pickup' || 'delivery';
  
  // State initialization from URL params
  const [locationInput, setLocationInput] = useState(location || '');
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (dateStringFromUrl) {
      const date = new Date(dateStringFromUrl); // Directly parse the ISO string
      if (!isNaN(date.getTime())) { // Check if the parsed date is valid
        return date;
      }
    }
    return null;
  });
  const [priceRange, setPriceRange] = useState<[number, number]>([
    Number(minPrice) || 0,
    Number(maxPrice) || 1000
  ]);
  const [selectedDeliveryType, setSelectedDeliveryType] = useState<'delivery' | 'pickup'>(deliveryType);
  const [selectedOccasions, setSelectedOccasions] = useState<string[]>(
    searchParams.getAll('occasions').length > 0 
      ? searchParams.getAll('occasions')
      : [...OCCASIONS]
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    searchParams.getAll('categories').length > 0
      ? searchParams.getAll('categories')
      : [...CATEGORIES]
  );
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  const PRODUCTS_PER_PAGE = 24;
  const LOAD_MORE_COUNT = 12;
  const [isLocationProcessing, setIsLocationProcessing] = useState(false);
  const previousLocationRef = useRef<string | null>(null);

  // Handler for changing sort order
  const handleSortOrderChange = (newOrder: string) => {
    setSortOrder(newOrder);
    const newParams = new URLSearchParams(searchParams);
    if (newOrder && newOrder !== 'default') {
      newParams.set('sortBy', newOrder);
    } else {
      newParams.delete('sortBy');
    }
    setSearchParams(newParams, { replace: true });
  };

  /**
   * Handles location selection from the search input
   * Updates coordinates and triggers a refetch of search results
   */
  const handleLocationSelect = useCallback((place: { formattedAddress: string; coordinates: { lat: number; lng: number } }) => {
    previousLocationRef.current = place.formattedAddress;
    setIsLocationProcessing(true);
    
    setCoordinates(place.coordinates);
    
    const newParams = new URLSearchParams(searchParams);
    newParams.set('location', place.formattedAddress);
    newParams.set('lat', place.coordinates.lat.toString());
    newParams.set('lng', place.coordinates.lng.toString());
    setSearchParams(newParams, { replace: true });
    setLocationInput(place.formattedAddress);
    
    queryClient.invalidateQueries({ queryKey: ['search-products'] });
    
    setTimeout(() => {
      setIsLocationProcessing(false);
    }, 100);
  }, [searchParams, setSearchParams, queryClient]);

  // Debounced location handler
  const debouncedHandleLocationSelect = useCallback(
    debounce((place) => handleLocationSelect(place), 300),
    [handleLocationSelect]
  );

  /**
   * Location Effect
   * Handles location changes from URL parameters
   * Sets default coordinates for Sydney if none provided
   */
  useEffect(() => {
    if (isLocationProcessing) return;
    
    const locationFromParams = searchParams.get('location');
    const latFromParams = searchParams.get('lat');
    const lngFromParams = searchParams.get('lng');
    
    if (latFromParams && lngFromParams) {
      const lat = parseFloat(latFromParams);
      const lng = parseFloat(lngFromParams);
      
      if (!coordinates || coordinates.lat !== lat || coordinates.lng !== lng) {
        setCoordinates({ lat, lng });
        if (locationFromParams) previousLocationRef.current = locationFromParams;
      }
    } else if (!coordinates) {
      previousLocationRef.current = "Sydney, NSW, Australia";
      setLocationInput("Sydney, NSW, Australia");
      setCoordinates({ lat: -33.8688, lng: 151.2093 });
    }
  }, [searchParams, coordinates, isLocationProcessing, setLocationInput]);

  // Handle price range changes with proper type conversion
  const handlePriceRangeChange = (value: number[]) => {
    setPriceRange([value[0], value[1]] as [number, number]);
  };

  // Format price range for display
  const formatPriceRange = () => {
    if (priceRange[1] === 500) {
      return `$${priceRange[0]} - $500+`;
    }
    return `$${priceRange[0]} - $${priceRange[1]}`;
  };

  /**
   * Products Query
   * Fetches and filters products based on location and selected filters
   */
  const { data: queryResult, isLoading: productsLoading } = useQuery({
    queryKey: ['search-products', location, coordinates ? `${coordinates.lat},${coordinates.lng}` : null, selectedCategories, selectedOccasions, priceRange, selectedDeliveryType, dateStringFromUrl, sortOrder, currentPage, groupBySizeVariant],
    queryFn: async () => {
      try {
        // 1. Fetch active AND APPROVED stores
        const { data: storesData, error: storeError } = await supabase
          .from('store_profiles')
        .select(
          `
            id,
            store_name,
            description,
            coordinates,
            geocoded_address,
            delivery_radius,
            business_hours,
            storefront_image_url,
            cover_image_url,
            suburb,
            state,
            same_day_enabled,
            delivery_days,
            delivery_cutoff_times,
            non_sameday_days,
            store_status,
            approval_status 
          `
        )
          .eq('store_status', 'active' as any)
          .eq('approval_status', 'approved' as any);

        if (storeError || !storesData) {
          console.error('Error fetching stores:', storeError);
          return [];
        }

        // Process stores with proper type assertions
        let filteredStores = (storesData as unknown as RawStoreProfile[]).map(store => {
          // Process each store
          const processedStore: ProcessedStore = {
            ...store,
            // Convert string fields to numbers
            delivery_radius: parseFloat(String(store.delivery_radius)),
            coordinates: null as { lat: number; lng: number } | null,
            calculated_distance: undefined,
            non_sameday_days: store.non_sameday_days || []
          };

          // Use coordinates column first, then fall back to geocoded_address
          if (store.coordinates && store.coordinates.lat && store.coordinates.lng) {
            processedStore.coordinates = {
              lat: Number(store.coordinates.lat),
              lng: Number(store.coordinates.lng)
            };
          } else if (store.geocoded_address) {
            try {
              const addressData = typeof store.geocoded_address === 'string'
                ? JSON.parse(store.geocoded_address)
                : store.geocoded_address;

              if (addressData?.geometry?.location) {
                processedStore.coordinates = {
                  lat: Number(addressData.geometry.location.lat),
                  lng: Number(addressData.geometry.location.lng)
                };
              }
            } catch (e) {
              console.error('Error parsing geocoded_address:', e);
            }
          }

          return processedStore;
        });

        // Filter stores by delivery radius
        if (coordinates && selectedDeliveryType === 'delivery') {
          filteredStores = filteredStores.filter(store => {
            if (!store.delivery_radius) return false;
            if (!store.coordinates) return false;

            const distance = haversineDistance(coordinates, store.coordinates);
            // Use a type assertion to add the calculated_distance property
            (store as any).calculated_distance = distance;
            const radiusInKm = Number(store.delivery_radius);

            return distance <= radiusInKm;
          });
        }

        if (filteredStores.length === 0) return [];

        // 3. Fetch products from filtered stores, ensuring products are also active
        let productsQuery = supabase
          .from('products')
          .select(`
            *,
            store:store_profiles!inner(*),
            sizes:product_sizes(
              id,
              size_name,
              price,
              is_active,
              main_image_url,
              gallery_image_urls
            ),
            occasions:product_occasions(
              occasion_id,
              occasion:occasions(
                id,
                name
              )
            )
          `)
          .in('store_id', filteredStores.map(store => store.id) as any[])
          .eq('active', true as any);

        // ✅ FIXED: Always order by created_at for consistent base ordering
        // Price sorting will be applied after variant creation
        productsQuery = productsQuery.order('created_at', { ascending: false });

        // Add pagination - fetch more than needed to check if there are more products
        const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
        const endIndex = startIndex + PRODUCTS_PER_PAGE + 1; // +1 to check if there are more
        productsQuery = productsQuery.range(startIndex, endIndex - 1);

        const { data: productsData, error: productsError } = await productsQuery;

        if (productsError || !productsData) {
          console.error('Error fetching products:', productsError);
          return [];
        }

        // Debug log to check raw data
        console.log("Raw products data:", productsData);

        // Process products with proper type assertions
        const processedProducts = (productsData as unknown as RawProduct[]).map(product => {
          const store = filteredStores.find(s => s.id === product.store_id);
          if (!store) return null;

          // Extract and process occasions
          const occasions = product.occasions?.map((o: any) => {
            return o.occasion?.name;
          }).filter(Boolean) as string[] || [];
          
          console.log('Processed occasions for product:', {
            productId: product.id,
            productTitle: product.title,
            rawOccasions: product.occasions,
            processedOccasions: occasions
          });
          
          return {
            ...product,
            price: Number(product.price),
            store,
            sizes: product.sizes?.map(size => ({
              ...size,
              name: size.name,
              price: Number(size.price)
            })).filter(s => s.is_active) || [],
            occasions
          } as ProcessedProduct;
        }).filter((p): p is ProcessedProduct => p !== null);

        // Apply filters
        let filteredProducts = processedProducts;

        // Price filter
        filteredProducts = filteredProducts.filter(product => {
          const basePrice = product.price;
          const baseInRange = basePrice >= priceRange[0] && basePrice <= priceRange[1];
          
          const hasSizesInRange = product.sizes?.some(size => {
            return size.price >= priceRange[0] && size.price <= priceRange[1];
          });
          
          return baseInRange || hasSizesInRange;
        });

        // Category filter
        if (selectedCategories.length > 0 && selectedCategories.length < CATEGORIES.length) {
          filteredProducts = filteredProducts.filter(product => 
            product.category && selectedCategories.includes(product.category)
          );
        }

        // Occasion filter
        if (selectedOccasions.length > 0 && selectedOccasions.length < OCCASIONS.length) {
          filteredProducts = filteredProducts.filter(product => {
            // Check if any of the product's occasions match the selected occasions
            return product.occasions?.some(occ => selectedOccasions.includes(occ)) ?? false;
          });
        }

        // Delivery date filter - Apply same-day delivery cutoff filter
        if (dateStringFromUrl && selectedDeliveryType === 'delivery') {
          const selectedDate = new Date(dateStringFromUrl);
          const today = new Date();
          const isSelectedDateToday = isDateToday(selectedDate);
          const dayOfWeekLower = getDayOfWeekLowercase(selectedDate);
          const dayOfWeekCapitalized = getDayOfWeekCapitalized(selectedDate);
          
          /**
           * Delivery filter logic for same-day and future-date availability
           * 
           * Data model explanation:
           * - same_day_enabled: Boolean flag indicating if the store offers same-day delivery
           * - delivery_days: Array of days of the week when same-day delivery is available
           * - delivery_cutoff_times: Object with day names as keys and cutoff times as values
           *   Example: {"Monday": "13:00", "Tuesday": "13:00"}
           * - non_sameday_days: Array of days when non-same-day (future) delivery is available
           * 
           * Filtering rules:
           * 1. For today (same-day):
           *    - Store must have same_day_enabled = true
           *    - Today's day must be in delivery_days
           *    - Current time must be before the cutoff time in delivery_cutoff_times
           * 
           * 2. For future dates:
           *    - Use non_sameday_days if available, otherwise fall back to delivery_days
           *    - Selected day must be in the applicable days array
           * 
           * 3. No date selected (Any Date):
           *    - No date-specific filtering is applied
           * 
           * Note: Day names have inconsistent capitalization in the database,
           * so we check both lowercase and capitalized versions for compatibility.
           */
          
          filteredProducts = filteredProducts.filter(product => {
            // Skip products without store info
            if (!product.store) return false;
            
            const store = product.store;
            
            // If today is selected, apply cutoff time filtering
            if (isSelectedDateToday) {
              // Check if same day delivery is enabled and store is open on this day
              const isSameDayEnabled = store.same_day_enabled === true;
              const isStoreDayInDeliveryDays = isDayInArray(dayOfWeekLower, store.delivery_days);
              
              if (isSameDayEnabled && isStoreDayInDeliveryDays) {
                // Get cutoff time for today
                const cutoffTimeStr = findCutoffTime(dayOfWeekLower, store.delivery_cutoff_times);
                
                if (cutoffTimeStr) {
                  // Format current time as HH:mm for comparison
                  const currentTimeStr = getCurrentTimeFormatted();
                  
                  // If after cutoff time, remove this product
                  if (currentTimeStr > cutoffTimeStr) {
                return false;
              }
                }
              } else {
                // Either same-day not enabled or store not open today..
                if (!isSameDayEnabled) {
                return false;
              }
                if (!isStoreDayInDeliveryDays) {
                  return false;
                }
              }
            } else if (dateStringFromUrl) {
              // Future date selected, check non-same-day delivery availability
              
              // Check if non_sameday_days exists and has the day, otherwise fall back to delivery_days
              const hasNonSameDayData = Array.isArray(store.non_sameday_days) && store.non_sameday_days.length > 0;
              const daysToCheck = hasNonSameDayData ? store.non_sameday_days : store.delivery_days;
              
              const isDayAvailable = isDayInArray(dayOfWeekLower, daysToCheck);
              
              if (!isDayAvailable) {
                return false;
              }
            }
            // If no date selected or passed all checks, include the product
            return true;
          });
        }

        // Transform to product cards
        const allVariants = filteredProducts.flatMap(product => {
          const baseProduct: ProductCard = {
            id: product.id,
            title: product.title,
            description: product.description,
            price: product.price,
            category: product.category,
            images: [product.main_image_url, ...(product.gallery_image_urls || [])].filter((img): img is string => !!img),
            occasions: product.occasions || [],
            store: {
              id: product.store.id,
              store_name: product.store.store_name,
              coordinates: product.store.coordinates,
              suburb: product.store.suburb,
              state: product.store.state,
              delivery_radius: product.store.delivery_radius,
              calculated_distance: product.store.calculated_distance
            }
          };

          if (!product.sizes?.length) {
            return [baseProduct];
          }

          const sizesInRange = product.sizes.filter(size => {
            return size.price >= priceRange[0] && size.price <= priceRange[1];
          });

          // If groupBySizeVariant is true, only show one card per product (the cheapest size)
          if (groupBySizeVariant && sizesInRange.length) {
            const cheapestSize = sizesInRange.reduce((min, size) => 
              size.price < min.price ? size : min
            );
            return [{
              ...baseProduct,
              price: cheapestSize.price,
              occasions: product.occasions || [],
              selectedVariant: {
                id: cheapestSize.id,
                size: cheapestSize.size_name,
                size_name: cheapestSize.size_name,
                price: cheapestSize.price,
                is_active: cheapestSize.is_active
              },
              uniqueId: `${product.id}-${cheapestSize.id}-${Date.now()}-${Math.random()}`
            }];
          }

          // If groupBySizeVariant is false, show all size variants as separate cards
          return sizesInRange.length ? sizesInRange.map(size => {
            console.log(`Creating variant for size:`, size); // Debug log
            return {
              ...baseProduct,
              price: size.price,
              occasions: product.occasions || [],
              selectedVariant: {
                id: size.id,
                size: size.size_name, // ✅ FIXED: Use size_name instead of name
                size_name: size.size_name, // ✅ FIXED: Use size_name instead of name
                price: size.price,
                is_active: size.is_active
              },
              uniqueId: `${product.id}-${size.id}-${Date.now()}-${Math.random()}`
            };
          }) : [baseProduct];
        });

        // ✅ FIXED: Apply sorting after variant creation for correct price ordering
        console.log('Sorting products with sortOrder:', sortOrder, 'allVariants count:', allVariants.length);
        let sortedVariants = allVariants;
        if (sortOrder === 'price_asc') {
          sortedVariants = allVariants.sort((a, b) => a.price - b.price);
          console.log('Applied price_asc sorting');
        } else if (sortOrder === 'price_desc') {
          sortedVariants = allVariants.sort((a, b) => b.price - a.price);
          console.log('Applied price_desc sorting');
        } else if (sortOrder === 'newest') {
          // Newest first - keep original created_at desc order from database
          sortedVariants = allVariants;
          console.log('Applied newest first sorting');
        } else {
          // Default sorting: Fair Mix - Round robin by florist (max 2-3 per florist per cycle)
          const maxPerFloristPerCycle = 2;
          const floristGroups = new Map<string, typeof allVariants>();
          
          // Group products by florist
          allVariants.forEach(product => {
            const floristId = product.store?.id || 'unknown';
            if (!floristGroups.has(floristId)) {
              floristGroups.set(floristId, []);
            }
            floristGroups.get(floristId)!.push(product);
          });
          
          // Round-robin distribution
          const fairMixProducts: typeof allVariants = [];
          let maxRounds = Math.max(...Array.from(floristGroups.values()).map(group => Math.ceil(group.length / maxPerFloristPerCycle)));
          
          for (let round = 0; round < maxRounds; round++) {
            // Randomize florist order each round for variety
            const floristIds = Array.from(floristGroups.keys()).sort(() => Math.random() - 0.5);
            
            floristIds.forEach(floristId => {
              const products = floristGroups.get(floristId)!;
              const startIndex = round * maxPerFloristPerCycle;
              const endIndex = startIndex + maxPerFloristPerCycle;
              const productsForThisRound = products.slice(startIndex, endIndex);
              fairMixProducts.push(...productsForThisRound);
            });
          }
          
          sortedVariants = fairMixProducts;
          console.log('Using Fair Mix sorting (round-robin by florist)');
        }
        
        // Handle pagination - check if we have more products than requested
        const hasMore = sortedVariants.length > PRODUCTS_PER_PAGE;
        const productsToReturn = hasMore ? sortedVariants.slice(0, PRODUCTS_PER_PAGE) : sortedVariants;
        
        return {
          products: productsToReturn,
          hasMore,
          totalFetched: sortedVariants.length
        };

      } catch (error) {
        console.error('Error in product query:', error);
        return { products: [], hasMore: false, totalFetched: 0 };
      }
    },
    enabled: true  // Always enable to show products even without location
  });

  // Handle pagination state updates
  useEffect(() => {
    if (queryResult) {
      console.log('Updating products from query result. currentPage:', currentPage, 'products count:', queryResult.products?.length);
      if (currentPage === 1) {
        // First page - replace all products
        setAllProducts(queryResult.products || []);
        console.log('Set products for page 1:', queryResult.products?.length || 0);
      } else {
        // Additional pages - append to existing products
        setAllProducts(prev => [...prev, ...(queryResult.products || [])]);
        console.log('Appended products for page', currentPage);
      }
      setHasMoreProducts(queryResult.hasMore || false);
      setIsLoadingMore(false);
    }
  }, [queryResult, currentPage]);

  // Reset pagination when search parameters change (but not for sort order changes)
  useEffect(() => {
    console.log('Resetting pagination due to search filter change');
    setCurrentPage(1);
    setAllProducts([]);
    setHasMoreProducts(true);
  }, [location, coordinates, selectedCategories, selectedOccasions, priceRange, selectedDeliveryType, dateStringFromUrl]);

  // Separate effect for sort order changes - only reset page, keep existing products until new ones load
  useEffect(() => {
    console.log('Sort order changed, resetting page only:', sortOrder);
    setCurrentPage(1);
  }, [sortOrder]);

  // Load More function
  const handleLoadMore = () => {
    if (!isLoadingMore && hasMoreProducts) {
      setIsLoadingMore(true);
      setCurrentPage(prev => prev + 1);
    }
  };

  // Use allProducts for display instead of direct query result
  const products = allProducts;

  /**
   * Calculate category and occasion counts from filtered products
   */
  const categoryCountMap = products ? products.reduce((acc: Record<string, number>, product: any) => {
    const category = product.category || 'Uncategorized';
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) : {};

  const occasionCountMap = products ? products.reduce((acc: Record<string, number>, product: any) => {
    const productOccasions = product.occasions || [];
    
    console.log("Product occasions for counting:", {
      productId: product.id,
      productTitle: product.title,
      occasions: productOccasions
    });
    
    // Count each occasion
    productOccasions.forEach((occasionName: string) => {
      if (OCCASIONS.includes(occasionName as any)) {
        acc[occasionName] = (acc[occasionName] || 0) + 1;
      }
    });
    
    return acc;
  }, {} as Record<string, number>) : {};

  const isLoading = productsLoading;
  const hasResults = (products?.length ?? 0) > 0;

  // Update URL when filters change
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    
    // Update occasions
    newParams.delete('occasions');
    selectedOccasions.forEach(occasion => newParams.append('occasions', occasion));
    
    // Update categories
    newParams.delete('categories');
    selectedCategories.forEach(category => newParams.append('categories', category));
    
    // Update price range
    newParams.set('minPrice', priceRange[0].toString());
    newParams.set('maxPrice', priceRange[1].toString());
    
    // Update delivery type
    newParams.set('deliveryType', selectedDeliveryType);
    
    // Ensure sortOrder is also updated in URL (already handled by handleSortOrderChange, but good for consistency if other filters are here)
    if (sortOrder && sortOrder !== 'default') {
        newParams.set('sortBy', sortOrder);
    } else {
        newParams.delete('sortBy');
    }
    setSearchParams(newParams, { replace: true });
  }, [selectedOccasions, selectedCategories, priceRange, selectedDeliveryType, searchParams, setSearchParams, sortOrder]);

  const containerRef = useRef<HTMLDivElement>(null);

  let markers: { lat: number; lng: number; label: string; isInRange: boolean }[] = [];
  let markersInRange: { lat: number; lng: number; label: string; isInRange: boolean }[] = [];
  
  if (products && products.length > 0) {
    const markersMap = new Map();
    (products as any[]).forEach(product => {
      if (!product.store || !product.store.id) return;
      const store = product.store;
      let storeCoords = null;
      if (store.coordinates && typeof store.coordinates === 'object' && 'lat' in store.coordinates && 'lng' in store.coordinates) {
        storeCoords = { lat: store.coordinates.lat, lng: store.coordinates.lng };
      } else if (store.geocoded_address) {
        try {
          const parsedGeocode = typeof store.geocoded_address === 'string' ? JSON.parse(store.geocoded_address) : store.geocoded_address;
          if (parsedGeocode?.geometry?.location) {
            storeCoords = { lat: parsedGeocode.geometry.location.lat, lng: parsedGeocode.geometry.location.lng };
          } else if (parsedGeocode?.lat && parsedGeocode?.lng) {
            storeCoords = { lat: parsedGeocode.lat, lng: parsedGeocode.lng };
          }
        } catch (error) { console.error('Error parsing geocoded_address for product store map marker:', error); }
      }
      if (storeCoords) {
        const locationText = store.suburb && store.state ? `${store.suburb}, ${store.state}` : '';
        // Assuming all product stores shown are effectively "in range" for map display purposes
        markersMap.set(store.id, { lat: storeCoords.lat, lng: storeCoords.lng, label: `${store.store_name}${locationText ? ` (${locationText})` : ''}`, isInRange: true });
      }
    });
    markersInRange = Array.from(markersMap.values());
    markers = markersInRange; // For now, all product store markers are considered primary for the map view
  }

  useEffect(() => {
    if (coordinates) {
      queryClient.invalidateQueries({ queryKey: ['search-products'], exact: false });
    }
  }, [coordinates, queryClient]);

  return (
    <div className="min-h-screen bg-brand-accent">
      {/* Hidden emoji preloader to force emoji fonts to load */}
      <div style={{ display: 'none' }}>
        <span className="emoji">{EMOJIS.FLOWER}{EMOJIS.TARGET}{EMOJIS.TRUCK}{EMOJIS.LIGHTNING}{EMOJIS.CART}</span>
      </div>
      {isMobile ? (
        <div className="pb-20">
          <MobileHero 
            showFilterButton={true}
            onFilterClick={() => {
              const filterButton = document.querySelector('[data-filter-trigger="true"]');
              if (filterButton instanceof HTMLElement) {
                filterButton.click();
              }
            }}
          />
          
          {/* Map Container */}
          <div className="bg-[#f4f2f2] pb-0">
            {/* Map Section - Reduced padding */}
            <div className="px-4 pt-0 pb-1">
              {/* Map View with improved border */}
              <div className="h-[180px] relative rounded-xl overflow-hidden">
                <div className="absolute inset-0 rounded-xl overflow-hidden">
                  <MapView 
                    markers={markersInRange} 
                    searchedLocation={coordinates} 
                    allFlorists={markers}
                    className="w-full h-full"
                  />
                </div>
                <div className="absolute inset-0 rounded-xl border-2 border-gray-200 pointer-events-none"></div>
              </div>
            </div>
            
            {/* Hidden MobileFilterDropdown for the button to click */}
            <div className="hidden">
              <MobileFilterDropdown 
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                selectedOccasions={selectedOccasions}
                setSelectedOccasions={setSelectedOccasions}
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                selectedDate={selectedDate}
                handleDateSelect={(value) => {
                  if (!value) {
                    setSelectedDate(null);
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('date');
                    setSearchParams(newParams, { replace: true });
                  } else {
                    const date = value.toDate(getLocalTimeZone());
                    setSelectedDate(date);
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('date', date.toISOString());
                    setSearchParams(newParams, { replace: true });
                  }
                }}
                deliveryType={selectedDeliveryType}
                toggleDeliveryType={(type) => {
                  setSelectedDeliveryType(type);
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('deliveryType', type);
                  setSearchParams(newParams, { replace: true });
                }}
                categoryCountMap={categoryCountMap}
                occasionCountMap={occasionCountMap}
                deliveryCount={0}
                pickupCount={0}
                sortOrder={sortOrder}
                handleSortOrderChange={handleSortOrderChange}
                groupBySizeVariant={groupBySizeVariant}
                setGroupBySizeVariant={setGroupBySizeVariant}
              />
            </div>
          </div>
          
          {/* Products container - Reduced spacing */}
          <div className="bg-white border-t border-brand-primary/10">
            <div className="px-4 pt-2 pb-24">
              <MobileSearchResults 
                products={products || []}
                isLoadingProducts={productsLoading}
              />
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* Header text below navigation */}
          <div className="max-w-[1600px] mx-auto px-2 md:px-4 lg:px-8 pt-24 pb-4">
            <h1 className="text-[32px] md:text-[40px] lg:text-[48px] leading-[1.1] text-brand-primary font-suisse font-medium tracking-[-0.02em] md:whitespace-nowrap">
              Your Cities Best Florists <span className="emoji inline-block"><span className="animate-spin-slow inline-block" style={{ fontFamily: "sans-serif", WebkitAnimation: "spin 4s linear infinite", animation: "spin 3s linear infinite", display: "inline-block", transformOrigin: "center" }}>🌸</span></span> Ready to Checkout Now <span className="emoji" dangerouslySetInnerHTML={{ __html: EMOJIS.CART }} />
            </h1>
            <p className="text-[14px] md:text-[16px] text-brand-primary/80 font-suisse mt-2">
              Handpicked Florists - Same-Day Options - Date & Time Filtering - Secure Checkout - Order Updates
            </p>
          </div>

          <div className="max-w-[1600px] mx-auto px-2 md:px-4 lg:px-8 py-4">
            <MapView 
              markers={markersInRange} 
              searchedLocation={coordinates} 
              allFlorists={markers}
            />
          </div>

          <div className="max-w-[1600px] mx-auto px-2 md:px-4 lg:px-8 py-6">
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
              {/* Left Sidebar */}
              <div className="w-full lg:w-[280px] lg:shrink-0">
                <div className="sticky top-8">
                  {/* Filter Groups */}
                  <div className="space-y-2 font-suisse">
                    {/* Sort By Dropdown */}
                    <div className="py-1.5">
                      <h3 className="text-[15px] text-brand-primary mb-2 font-normal">Sort by:</h3>
                      <Select value={sortOrder} onValueChange={handleSortOrderChange}>
                        <SelectTrigger className="w-full bg-white border border-brand-primary h-10 text-[13px] rounded-lg px-3 focus:ring-brand-primary text-brand-secondary placeholder:text-brand-secondary font-suisse hover:bg-[#843329]/20">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Recommended</SelectItem>
                          <SelectItem value="newest">Newest First</SelectItem>
                          <SelectItem value="price_asc">Price: Low to High</SelectItem>
                          <SelectItem value="price_desc">Price: High to Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Group by Product Toggle */}
                    <div className="py-1.5">
                      <div className="flex items-center justify-between">
                        <label htmlFor="group-toggle" className="text-[15px] text-brand-primary font-normal cursor-pointer">
                          Group by Product
                        </label>
                        <button
                          id="group-toggle"
                          onClick={() => setGroupBySizeVariant(!groupBySizeVariant)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#843329] focus:ring-offset-2 ${
                            groupBySizeVariant ? 'bg-[#843329]' : 'bg-gray-400'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                              groupBySizeVariant ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      <p className="text-[12px] text-brand-primary/60 mt-1">
                        {groupBySizeVariant ? 'Showing one card per product' : 'Showing all size variants'}
                      </p>
                    </div>

                    {/* Delivery Type */}
                    <Button
                      onClick={() => setSelectedDeliveryType('delivery')}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-2 bg-white border-2 rounded-lg transition-colors",
                        selectedDeliveryType === 'delivery' 
                          ? "bg-[#967F81]/30 border-[#967F81] text-[#2C1810]" 
                          : "border-gray-300 hover:border-[#843329] text-[#2C1810]"
                      )}
                    >
                      <span className="text-[15px] font-normal">Delivery</span>
                    </Button>

                    <Button
                      onClick={() => setSelectedDeliveryType('pickup')}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-2 bg-white border-2 rounded-lg transition-colors",
                        selectedDeliveryType === 'pickup' 
                          ? "bg-[#967F81]/30 border-[#967F81] text-[#2C1810]" 
                          : "border-gray-300 hover:border-[#843329] text-[#2C1810]"
                      )}
                    >
                      <span className="text-[15px] font-normal">Pickup</span>
                    </Button>

                    {/* Location Search */}
                    <div className="py-1.5">
                      <LocationSearchInput
                        defaultValue={locationInput}
                        onPlaceSelected={debouncedHandleLocationSelect}
                        placeholder="Enter your delivery address..."
                        className="w-full bg-white border border-brand-primary h-10 text-[13px] rounded-lg px-3 focus:ring-brand-primary text-brand-secondary placeholder:text-brand-secondary font-suisse hover:bg-[#843329]/20"
                      />
                    </div>

                    {/* Date Picker */}
                    <div className="py-1.5">
                      <DateFilter
                        date={selectedDate || undefined}
                        setDate={(date: Date | undefined) => {
                          setSelectedDate(date || null);
                          const newParams = new URLSearchParams(searchParams);
                          if (date) {
                            newParams.set('date', date.toISOString());
                          } else {
                            newParams.delete('date');
                          }
                          setSearchParams(newParams, { replace: true });
                        }}
                      />
                    </div>

                    {/* Price Range */}
                    <div className="py-1.5">
                      <h3 className="text-[15px] text-brand-primary mb-2 font-normal">Budget</h3>
                      <Slider
                        defaultValue={[0, 1000]}
                        max={1000}
                        min={0}
                        step={25}
                        value={priceRange}
                        onValueChange={handlePriceRangeChange}
                        className="[&_[data-orientation=horizontal]]:h-2 [&_[data-orientation=horizontal]]:bg-gray-300 [&_span[role=slider]]:h-5 [&_span[role=slider]]:w-5 [&_span[role=slider]]:bg-[#967F81] [&_span[role=slider]]:border-2 [&_span[role=slider]]:border-white [&_span[role=slider]]:shadow-md [&_[data-orientation=horizontal]_[role=slider]~[role=slider]~div]:bg-[#967F81]"
                      />
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[13px] text-brand-primary font-normal">${priceRange[0]}</span>
                        <span className="text-[13px] text-brand-primary font-normal">{priceRange[1] === 1000 ? "$1000+" : `$${priceRange[1]}`}</span>
                      </div>
                    </div>

                    {/* Categories */}
                    <div className="py-1.5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-[15px] text-brand-primary font-normal">Categories</h3>
                        <button 
                          onClick={() => {
                            if (selectedCategories.length === CATEGORIES.length) {
                              setSelectedCategories([]);
                            } else {
                              setSelectedCategories([...CATEGORIES]);
                            }
                          }}
                          className="text-[13px] text-brand-primary hover:text-brand-primary font-normal"
                        >
                          {selectedCategories.length === CATEGORIES.length ? 'Clear' : 'Select All'}
                        </button>
                      </div>
                      <div className="space-y-1.5">
                        {CATEGORIES.map((category) => (
                          <div key={category} className={cn(
                            "flex items-center justify-between group cursor-pointer p-2.5 rounded-lg border-2 transition-colors",
                            selectedCategories.includes(category) 
                              ? "bg-[#967F81]/30 border-[#967F81]" 
                              : "bg-white border-gray-300 hover:border-[#843329]"
                          )}>
                            <label className="flex items-center gap-2 flex-1 cursor-pointer">
                              <Checkbox
                                checked={selectedCategories.includes(category)}
                                onCheckedChange={(checked) => {
                                  const newCategories = checked
                                    ? [...selectedCategories, category]
                                    : selectedCategories.filter(c => c !== category);
                                  setSelectedCategories(newCategories);
                                }}
                                className="h-4 w-4 border-2 border-gray-400 rounded-sm data-[state=checked]:bg-[#2C1810] data-[state=checked]:border-[#2C1810] data-[state=checked]:text-white"
                              />
                              <span className={cn(
                                "text-[15px] font-normal",
                                selectedCategories.includes(category) ? "text-[#2C1810]" : "text-gray-700"
                              )}>{category}</span>
                            </label>
                            <span className={cn(
                              "text-[13px] font-normal",
                              selectedCategories.includes(category) ? "text-[#2C1810]" : "text-gray-500"
                            )}>{(categoryCountMap as any)[category] || 0}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Occasions Filter Section */}
                    <div className="py-1.5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-[15px] text-brand-primary font-normal">Occasions</h3>
                        <button 
                          onClick={() => {
                            if (selectedOccasions.length === OCCASIONS.length) {
                              setSelectedOccasions([]);
                            } else {
                              setSelectedOccasions([...OCCASIONS]);
                            }
                          }}
                          className="text-[13px] text-brand-primary hover:text-brand-primary font-normal"
                        >
                          {selectedOccasions.length === OCCASIONS.length ? 'Clear' : 'Select All'}
                        </button>
                      </div>
                      <div className="space-y-1.5">
                        {OCCASIONS.map((occasion) => (
                          <div key={occasion} className={cn(
                            "flex items-center justify-between group cursor-pointer p-2.5 rounded-lg border-2 transition-colors",
                            selectedOccasions.includes(occasion) 
                              ? "bg-[#967F81]/30 border-[#967F81]" 
                              : "bg-white border-gray-300 hover:border-[#843329]"
                          )}>
                            <label className="flex items-center gap-2 flex-1 cursor-pointer">
                              <Checkbox
                                checked={selectedOccasions.includes(occasion)}
                                onCheckedChange={(checked) => {
                                  const newOccasions = checked
                                    ? [...selectedOccasions, occasion]
                                    : selectedOccasions.filter(o => o !== occasion);
                                  setSelectedOccasions(newOccasions);
                                }}
                                className="h-4 w-4 border-2 border-gray-400 rounded-sm data-[state=checked]:bg-[#2C1810] data-[state=checked]:border-[#2C1810] data-[state=checked]:text-white"
                              />
                              <span className={cn(
                                "text-[15px] font-normal",
                                selectedOccasions.includes(occasion) ? "text-[#2C1810]" : "text-gray-700"
                              )}>{occasion}</span>
                            </label>
                            <span className={cn(
                              "text-[13px] font-normal",
                              selectedOccasions.includes(occasion) ? "text-[#2C1810]" : "text-gray-500"
                            )}>{(occasionCountMap as any)[occasion] || 0}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Grid Section with white background */}
              <div className="flex-1 flex justify-end">
                <div className="bg-white p-6 rounded-lg shadow-md w-[1100px]">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-96 w-full">
                      <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                    </div>
                  ) : hasResults ? (
                    <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8 w-full">
                      {(products as any[]).map((product) => {
                        if (!product || !product.id) return null;
                        const productImage = product.images?.[0] || '/images/placeholder-product.png';
                        const productTitle = product?.title || 'Product';
                        const productPrice = product?.price || 0;
                        const productVariant = product?.selectedVariant;
                        return (
                          <Link
                            key={product.uniqueId || product.id || Math.random().toString()}
                            to={`/product/${product.id}${productVariant ? `/${productVariant.id}` : ''}`}
                            className="group font-suisse w-full border border-brand-primary rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300"
                          >
                            <div className="aspect-[4/5] bg-brand-accent overflow-hidden">
                              <img 
                                src={productImage} 
                                alt={productTitle}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/images/placeholder-product.png';
                                }}
                              />
                            </div>
                            <div className="p-3 bg-white flex flex-col justify-between h-[150px]">
                              <div className="space-y-1.5">
                                <h3 className="text-[16px] text-brand-primary font-normal leading-none">
                                  {productTitle}
                                </h3>
                                {productVariant && (
                                  <p className="text-[16px] text-[#2C1810] font-normal leading-none">
                                    {productVariant.size_name || productVariant.size || 'Standard'}
                                  </p>
                                )}
                                <p className="text-[16px] text-brand-primary font-normal leading-none mb-1">
                                  {groupBySizeVariant && 'from '}${productPrice}
                                </p>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                {product.store ? (
                                  <FloristNameWithPopup florist={product.store} />
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#967F81]/20 text-[13px] text-[#2C1810] rounded-full font-normal">
                                    <StoreIcon className="w-3 h-3" />
                                    Store
                                  </span>
                                )}
                                {product.store?.suburb && product.store?.state ? (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#967F81]/30 border border-[#967F81] text-[13px] text-[#2C1810] rounded-full font-normal">
                                    <MapPin className="w-3 h-3" />
                                    {product.store.suburb}, {product.store.state}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#967F81]/30 border border-[#967F81] text-[13px] text-[#2C1810] rounded-full font-normal">
                                    <MapPin className="w-3 h-3" />
                                    Rosebery, NSW
                                  </span>
                                )}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                    
                    {/* Load More Button */}
                    {hasMoreProducts && (
                      <div className="flex justify-center mt-8">
                        <button
                          onClick={handleLoadMore}
                          disabled={isLoadingMore}
                          className="inline-flex items-center gap-2 px-8 py-3 bg-[#967F81] text-white rounded-full font-medium hover:bg-[#843329] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoadingMore ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Loading more...
                            </>
                          ) : (
                            <>
                              Load More Products
                              <span className="text-sm opacity-80">({LOAD_MORE_COUNT} more)</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                    
                    {/* Products count indicator */}
                    <div className="text-center mt-4 text-sm text-gray-600">
                      Showing {products.length} products
                      {!hasMoreProducts && products.length > PRODUCTS_PER_PAGE && (
                        <span className="ml-1">• All results loaded</span>
                      )}
                    </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-96 text-center font-suisse">
                      <p className="text-[14px] text-brand-primary mb-1 font-normal">
                        No arrangements found
                      </p>
                      <p className="text-[12px] text-brand-primary font-normal">
                        Try adjusting your filters or search for a different location
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}